import { LastWatchedExt } from '@shared/types';
import { WordBatchStatus } from '@shared/types/words';
import { collectShowIdentifiers } from '@shared/utils/show';

export function getProgressPercent(item?: LastWatchedExt): number | undefined {
  if (!item) {
    return undefined;
  }

  const statuses = item.details?.cardStatuses || {};
  const answered = Object.keys(statuses).length;
  const remembered = Object.values(statuses).filter((status) => status === 'remembered').length;
  const loaded = Number(item.details?.loadedCardsTotal) || 0;
  const active = Number(item.details?.activeCardsLimit) || 0;
  const total = Math.max(active || loaded, answered);

  if (!total) {
    return undefined;
  }

  return Math.max(0, Math.min(100, Math.round((remembered / total) * 100)));
}

export function isProgressLoading(item?: LastWatchedExt): boolean {
  if (!item) {
    return false;
  }

  const batches = item.wordbatches || [];
  const isAutoPhaseBatch = (part?: number) => typeof part === 'number' && part > 0 && part <= 2;

  // Auto phase (8 + 22): both Processing and recent Created should lock.
  if (batches.some((batch) => isAutoPhaseBatch(batch.part) && batch.status === WordBatchStatus.Processing)) {
    return true;
  }

  // Extra phase (part > 2): lock only when actual processing is running.
  if (batches.some((batch) => !isAutoPhaseBatch(batch.part) && batch.status === WordBatchStatus.Processing)) {
    return true;
  }

  // "Created" means queue/waiting. It should count as loading for all stages
  // (first 8 words, 22 words, and further chunks), but only in a recent window
  // to avoid stale historical rows.
  const hasCreatedBatch = batches.some((batch) => isAutoPhaseBatch(batch.part) && batch.status === WordBatchStatus.Created);
  if (!hasCreatedBatch) {
    return false;
  }

  const ts = new Date(item.lastUsedAt || item.updatedAt).getTime();
  if (!Number.isFinite(ts)) {
    return false;
  }
  const CREATED_LOADING_TTL_MS = 20 * 60 * 1000;
  return (Date.now() - ts) < CREATED_LOADING_TTL_MS;
}

export function isSameProgressScope(
  item?: LastWatchedExt | null,
  active?: LastWatchedExt | null
): boolean {
  if (!item || !active) {
    return false;
  }

  const itemIds = collectShowIdentifiers(item.show as any, [item.screenmediaId]);
  const activeIds = new Set(collectShowIdentifiers(active.show as any, [active.screenmediaId]));
  if (!itemIds.length || !Array.from(itemIds).some((id) => activeIds.has(id))) {
    return false;
  }

  const itemSeason = typeof item.season === 'number' ? item.season : null;
  const activeSeason = typeof active.season === 'number' ? active.season : null;
  if (itemSeason !== activeSeason) {
    return false;
  }

  const itemEpisode = typeof item.episode === 'number' ? item.episode : null;
  const activeEpisode = typeof active.episode === 'number' ? active.episode : null;
  return itemEpisode === activeEpisode;
}
